import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { getSessionToken } from "../hooks/useSession";
import { AgentSearchSelect } from "./fields/AgentSearchSelect";
import { Button } from "./ui/Button";
import { Card } from "./ui/Card";

const API_BASE = import.meta.env.VITE_API_URL || "http://localhost:4000";

interface AgentPreview {
  id: string;
  email: string;
  displayName: string | null;
  npn: string | null;
}

interface DownlineRow {
  id: string;
  email: string;
  displayName: string | null;
}

interface UplineInfo {
  id: string;
  email: string;
  displayName: string;
}

interface AgentOperationsPanelProps {
  agentId: string;
  displayName: string;
  email: string;
  upline?: UplineInfo | null;
  archivedAt?: string | null;
  onUpdated: () => void | Promise<void>;
  onArchived?: () => void;
}

export function AgentOperationsPanel({
  agentId,
  displayName,
  email,
  upline: initialUpline,
  archivedAt: initialArchivedAt,
  onUpdated,
  onArchived,
}: AgentOperationsPanelProps) {
  const token = getSessionToken();
  const isArchived = Boolean(initialArchivedAt);

  const [upline, setUpline] = useState<UplineInfo | null | undefined>(initialUpline);
  const [downlines, setDownlines] = useState<DownlineRow[]>([]);
  const [loadingDownlines, setLoadingDownlines] = useState(true);

  const [uplinePickId, setUplinePickId] = useState("");
  const [downlinePickId, setDownlinePickId] = useState("");
  const [duplicateAgentId, setDuplicateAgentId] = useState("");
  const [duplicatePreview, setDuplicatePreview] = useState<AgentPreview | null>(null);
  const [mergeConfirmed, setMergeConfirmed] = useState(false);
  const [archiveConfirmed, setArchiveConfirmed] = useState(false);

  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    setUpline(initialUpline);
  }, [initialUpline]);

  const loadDownlines = useCallback(async () => {
    if (!token) return;
    setLoadingDownlines(true);
    try {
      const res = await fetch(`${API_BASE}/api/agents/${agentId}/downlines`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const json = await res.json();
      if (res.ok && json.success) {
        setDownlines(json.data as DownlineRow[]);
      }
    } finally {
      setLoadingDownlines(false);
    }
  }, [agentId, token]);

  useEffect(() => {
    void loadDownlines();
  }, [loadDownlines]);

  useEffect(() => {
    if (!duplicateAgentId || !token) {
      setDuplicatePreview(null);
      setMergeConfirmed(false);
      return;
    }

    let cancelled = false;
    (async () => {
      const res = await fetch(`${API_BASE}/api/agents/${duplicateAgentId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const json = await res.json();
      if (cancelled || !res.ok || !json.success) {
        if (!cancelled) setDuplicatePreview(null);
        return;
      }
      const agent = json.data as AgentPreview;
      setDuplicatePreview({
        id: agent.id,
        email: agent.email,
        displayName: agent.displayName ?? null,
        npn: agent.npn ?? null,
      });
    })();

    return () => {
      cancelled = true;
    };
  }, [duplicateAgentId, token]);

  useEffect(() => {
    setMergeConfirmed(false);
  }, [duplicateAgentId]);

  async function handleSetUpline() {
    if (!token || !uplinePickId) return;
    setBusy("upline");
    setError(null);
    setSuccess(null);
    try {
      const res = await fetch(`${API_BASE}/api/agents/${agentId}/upline`, {
        method: "PATCH",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ uplineUserId: uplinePickId }),
      });
      const json = await res.json();
      if (!res.ok || !json.success) throw new Error(json.error || "Failed to set upline");
      setUpline(json.data.upline ?? null);
      setUplinePickId("");
      setSuccess("Upline updated.");
      await onUpdated();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to set upline");
    } finally {
      setBusy(null);
    }
  }

  async function handleClearUpline() {
    if (!token) return;
    setBusy("clear-upline");
    setError(null);
    setSuccess(null);
    try {
      const res = await fetch(`${API_BASE}/api/agents/${agentId}/upline`, {
        method: "PATCH",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ uplineUserId: null }),
      });
      const json = await res.json();
      if (!res.ok || !json.success) throw new Error(json.error || "Failed to clear upline");
      setUpline(null);
      setSuccess("Upline removed.");
      await onUpdated();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to clear upline");
    } finally {
      setBusy(null);
    }
  }

  async function handleAddDownline() {
    if (!token || !downlinePickId) return;
    setBusy("add-downline");
    setError(null);
    setSuccess(null);
    try {
      const res = await fetch(`${API_BASE}/api/agents/${agentId}/downlines`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ downlineAgentId: downlinePickId }),
      });
      const json = await res.json();
      if (!res.ok || !json.success) throw new Error(json.error || "Failed to add downline");
      setDownlinePickId("");
      setSuccess("Downline assigned.");
      await loadDownlines();
      await onUpdated();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to add downline");
    } finally {
      setBusy(null);
    }
  }

  async function handleRemoveDownline(downlineId: string) {
    if (!token) return;
    setBusy(`remove-${downlineId}`);
    setError(null);
    setSuccess(null);
    try {
      const res = await fetch(`${API_BASE}/api/agents/${agentId}/downlines/${downlineId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      const json = await res.json();
      if (!res.ok || !json.success) throw new Error(json.error || "Failed to remove downline");
      setSuccess("Downline link removed.");
      await loadDownlines();
      await onUpdated();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to remove downline");
    } finally {
      setBusy(null);
    }
  }

  async function handleArchive() {
    if (!token || !archiveConfirmed || isArchived) return;
    setBusy("archive");
    setError(null);
    setSuccess(null);
    try {
      const res = await fetch(`${API_BASE}/api/agents/${agentId}/archive`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      const json = await res.json();
      if (!res.ok || !json.success) throw new Error(json.error || "Failed to archive agent");
      setSuccess("Agent archived.");
      onArchived?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to archive agent");
    } finally {
      setBusy(null);
    }
  }

  async function handleUnarchive() {
    if (!token || !isArchived) return;
    setBusy("unarchive");
    setError(null);
    setSuccess(null);
    try {
      const res = await fetch(`${API_BASE}/api/agents/${agentId}/unarchive`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      const json = await res.json();
      if (!res.ok || !json.success) throw new Error(json.error || "Failed to restore agent");
      setSuccess("Agent restored from archive.");
      await onUpdated();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to restore agent");
    } finally {
      setBusy(null);
    }
  }

  async function handleMerge() {
    if (!token || !duplicateAgentId || !mergeConfirmed) return;
    setBusy("merge");
    setError(null);
    setSuccess(null);
    try {
      const res = await fetch(`${API_BASE}/api/agents/${agentId}/merge`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ duplicateAgentId }),
      });
      const json = await res.json();
      if (!res.ok || !json.success) {
        throw new Error(json.error || "Failed to merge agents");
      }

      const warnings = (json.data?.warnings ?? []) as string[];
      setSuccess(
        warnings.length > 0
          ? `Agents merged. ${warnings.join(" ")}`
          : "Agents merged successfully."
      );
      setDuplicateAgentId("");
      setDuplicatePreview(null);
      setMergeConfirmed(false);
      await loadDownlines();
      await onUpdated();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to merge agents");
    } finally {
      setBusy(null);
    }
  }

  const downlineIds = new Set(downlines.map((row) => row.id));

  return (
    <Card className="border-status-nigo-border bg-status-nigo-bg/50">
      <div className="space-y-8">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="text-lg font-semibold text-content-primary">Agent operations</h2>
            <span className="rounded-full border border-status-nigo-border bg-status-nigo-bg px-2.5 py-0.5 text-xs font-semibold uppercase tracking-wide text-status-nigo-text">
              Staff only
            </span>
          </div>
          <p className="mt-1 text-sm text-status-nigo-text/90">
            Manage hierarchy and merge duplicate records for{" "}
            <span className="font-medium text-content-primary">{displayName}</span> ({email}).
          </p>
        </div>

        {error && <p className="text-sm text-status-rejected-text">{error}</p>}
        {success && <p className="text-sm text-status-active-text">{success}</p>}

        {isArchived && (
          <p className="rounded-lg border border-status-nigo-border bg-status-nigo-bg/60 px-3 py-2 text-sm text-status-nigo-text">
            This agent is archived and hidden from the active agents list. Restore them to edit
            hierarchy or merge records.
          </p>
        )}

        {!isArchived && (
        <>
        <section className="space-y-4 border-t border-status-nigo-border/40 pt-6">
          <div>
            <h3 className="text-sm font-semibold uppercase tracking-wide text-content-primary">
              Hierarchy
            </h3>
            <p className="mt-1 text-sm text-content-muted">
              Set who this agent reports to, or assign agents as direct downlines.
            </p>
          </div>

          <div className="rounded-lg border border-border-default bg-surface-overlay p-4 space-y-3">
            <p className="text-xs uppercase tracking-wide text-content-muted">Direct upline</p>
            {upline ? (
              <div className="flex flex-wrap items-center justify-between gap-2">
                <Link
                  to={`/dashboard/agents/${upline.id}`}
                  className="text-sm font-medium text-content-primary hover:text-accent"
                >
                  {upline.displayName}
                </Link>
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  disabled={busy !== null}
                  onClick={() => void handleClearUpline()}
                >
                  {busy === "clear-upline" ? "Removing…" : "Remove upline"}
                </Button>
              </div>
            ) : (
              <p className="text-sm text-content-muted">No upline assigned.</p>
            )}

            <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
              <div className="flex-1 min-w-[220px]">
                <label className="mb-1.5 block text-sm font-medium text-content-primary">
                  Assign upline
                </label>
                <AgentSearchSelect
                  value={uplinePickId}
                  onChange={setUplinePickId}
                  token={token ?? ""}
                  placeholder="Search upline agent…"
                  excludeAgentId={agentId}
                  allowClear={false}
                />
              </div>
              <Button
                type="button"
                disabled={!uplinePickId || busy !== null}
                onClick={() => void handleSetUpline()}
              >
                {busy === "upline" ? "Saving…" : "Set upline"}
              </Button>
            </div>
          </div>

          <div className="rounded-lg border border-border-default bg-surface-overlay p-4 space-y-3">
            <p className="text-xs uppercase tracking-wide text-content-muted">Direct downlines</p>
            {loadingDownlines ? (
              <p className="text-sm text-content-muted">Loading downlines…</p>
            ) : downlines.length === 0 ? (
              <p className="text-sm text-content-muted">No direct downlines yet.</p>
            ) : (
              <ul className="divide-y divide-border-subtle rounded-lg border border-border-subtle">
                {downlines.map((downline) => (
                  <li
                    key={downline.id}
                    className="flex flex-wrap items-center justify-between gap-2 px-3 py-2"
                  >
                    <Link
                      to={`/dashboard/agents/${downline.id}`}
                      className="text-sm font-medium text-content-primary hover:text-accent"
                    >
                      {downline.displayName || downline.email}
                    </Link>
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      disabled={busy !== null}
                      onClick={() => void handleRemoveDownline(downline.id)}
                    >
                      {busy === `remove-${downline.id}` ? "Removing…" : "Remove"}
                    </Button>
                  </li>
                ))}
              </ul>
            )}

            <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
              <div className="flex-1 min-w-[220px]">
                <label className="mb-1.5 block text-sm font-medium text-content-primary">
                  Add downline
                </label>
                <AgentSearchSelect
                  value={downlinePickId}
                  onChange={setDownlinePickId}
                  token={token ?? ""}
                  placeholder="Search agent to add…"
                  excludeAgentId={agentId}
                  allowClear={false}
                />
              </div>
              <Button
                type="button"
                disabled={!downlinePickId || downlineIds.has(downlinePickId) || busy !== null}
                onClick={() => void handleAddDownline()}
              >
                {busy === "add-downline" ? "Adding…" : "Add downline"}
              </Button>
            </div>
          </div>
        </section>

        <section className="space-y-4 border-t border-border-subtle pt-6">
          <div>
            <h3 className="text-sm font-semibold uppercase tracking-wide text-content-primary">
              Merge duplicate
            </h3>
            <p className="mt-1 text-sm text-content-muted">
              Combine another record into this profile. Contracts, submissions, source links, and
              referral codes move here; the duplicate is deleted.
            </p>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <div className="rounded-lg border border-border-default bg-surface-overlay p-3">
              <p className="text-xs uppercase tracking-wide text-content-muted">Keep (this page)</p>
              <p className="mt-1 font-medium text-content-primary">{displayName}</p>
              <p className="text-sm text-content-muted">{email}</p>
            </div>
            <div className="rounded-lg border border-border-default bg-surface-overlay p-3">
              <p className="text-xs uppercase tracking-wide text-content-muted">Merge in (duplicate)</p>
              {duplicatePreview ? (
                <>
                  <p className="mt-1 font-medium text-content-primary">
                    {duplicatePreview.displayName || duplicatePreview.email}
                  </p>
                  <p className="text-sm text-content-muted">{duplicatePreview.email}</p>
                  {duplicatePreview.npn && (
                    <p className="text-xs text-content-muted mt-1">NPN {duplicatePreview.npn}</p>
                  )}
                </>
              ) : (
                <p className="mt-1 text-sm text-content-muted">Search and select a duplicate below.</p>
              )}
            </div>
          </div>

          <div className="max-w-md">
            <label className="mb-1.5 block text-sm font-medium text-content-primary">
              Duplicate agent
            </label>
            <AgentSearchSelect
              value={duplicateAgentId}
              onChange={setDuplicateAgentId}
              token={token ?? ""}
              placeholder="Search duplicate agent…"
              excludeAgentId={agentId}
              allowClear={false}
            />
          </div>

          {duplicatePreview && (
            <label className="flex items-start gap-2 text-sm text-content-primary">
              <input
                type="checkbox"
                className="mt-1"
                checked={mergeConfirmed}
                onChange={(event) => setMergeConfirmed(event.target.checked)}
              />
              <span>
                I understand this will permanently delete the duplicate agent record and cannot be
                undone.
              </span>
            </label>
          )}

          <Button
            type="button"
            variant="danger"
            disabled={!duplicateAgentId || !mergeConfirmed || busy !== null}
            onClick={() => void handleMerge()}
          >
            {busy === "merge" ? "Merging…" : "Merge into this agent"}
          </Button>
        </section>
        </>
        )}

        <section className="space-y-4 border-t border-status-nigo-border/40 pt-6">
          <div>
            <h3 className="text-sm font-semibold uppercase tracking-wide text-content-primary">
              Archive
            </h3>
            <p className="mt-1 text-sm text-status-nigo-text/90">
              {isArchived
                ? "Restore this agent to the active list and staff workflows."
                : "Hide this agent from the active list. Their history, contracts, and submissions are kept."}
            </p>
          </div>

          {isArchived ? (
            <Button
              type="button"
              variant="secondary"
              disabled={busy !== null}
              onClick={() => void handleUnarchive()}
            >
              {busy === "unarchive" ? "Restoring…" : "Restore agent"}
            </Button>
          ) : (
            <>
              <label className="flex items-start gap-2 text-sm text-content-primary">
                <input
                  type="checkbox"
                  className="mt-1"
                  checked={archiveConfirmed}
                  onChange={(event) => setArchiveConfirmed(event.target.checked)}
                />
                <span>
                  I understand this will hide the agent from the active list, disable portal login,
                  and deactivate their registration links.
                </span>
              </label>
              <Button
                type="button"
                variant="danger"
                disabled={!archiveConfirmed || busy !== null}
                onClick={() => void handleArchive()}
              >
                {busy === "archive" ? "Archiving…" : "Archive agent"}
              </Button>
            </>
          )}
        </section>
      </div>
    </Card>
  );
}
